import type { Response, StoreThis } from '@/types'
import type { ToastState } from '@/types/ui'
import type { CustomerSubscription } from '@/types/customer/subscriptions'

import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useUiStore } from '@/stores/ui'

import { clone, handleErrors } from '@/helpers'
import { goal }                from "@/plugins/metrika";

type SingleType = CustomerSubscription
type FromType = SingleType

const resource = 'subscriptions'
const storeId = `customer-${resource}`

export const useCustomerSubscriptionsStore = defineStore(storeId, () => {
  const ui = useUiStore()

  const singleItemIsLoading = ref(false)

  async function subscribe<T = FromType>(
    this: StoreThis,
    form: T,
    showToast = false
  ) {
    const options: Partial<ToastState> = {}
    let error = false
    let validationErrors: Partial<Record<keyof SingleType, string[]>> = {}
    let otherError = ''

    singleItemIsLoading.value = true

    const payload = clone(form)

    try {
      const {
        data: { success, errors },
      }: Response<SingleType> = await this.rest.post(`/c/${resource}`, payload)

      if (!success && errors) {
        validationErrors = errors.validation ?? {}
        otherError = errors.other ?? ''

        throw Error(handleErrors(errors))
      }

      options.type = 'success'
      options.text = 'Вы успешно подписались'

      goal('subscription')
    } catch (e) {
      error = true
      console.error(`[${storeId}/subscribe]`, e)

      options.text = 'Что-то пошло не так. Подписаться не удалось'
    } finally {
      singleItemIsLoading.value = false

      if (options.text && showToast) ui.showToast(options)
    }

    return { error, validationErrors, otherError }
  }

  async function confirmLoyalty(
    this: StoreThis,
    form: { phone: string; code: string }
  ) {
    let error = false
    let validationErrors: Partial<Record<string, string[]>> = {}
    let otherError = ''

    singleItemIsLoading.value = true

    const payload = clone(form)

    try {
      const {
        data: { success, errors },
      }: Response<SingleType> = await this.rest.post(
        `/c/${resource}/confirm`,
        payload
      )

      if (!success && errors) {
        validationErrors = errors.validation ?? {}
        otherError = errors.other ?? ''

        throw Error(handleErrors(errors))
      }
    } catch (e) {
      error = true
      console.error(`[${storeId}/confirmLoyalty]`, e)
    } finally {
      singleItemIsLoading.value = false
    }

    return { error, validationErrors, otherError }
  }

  return {
    singleItemIsLoading,
    subscribe,
    confirmLoyalty,
  }
})
